import { useState } from "react";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, Download } from "lucide-react";

export default function ReferenceCard() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", problem: "" });
  const [status, setStatus] = useState("idle");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setStatus("submitting");
    try {
      await api.post("/leads/reference-card", form);
      setStatus("success");
      toast.success("Reference card unlocked.");
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  return (
    <PageContainer testid="reference-card-page">
      <PageHeader
        overline="Reference Card"
        title="The 19-framework reference card."
        subtitle="One page. Every framework, every role assignment, every non-negotiable. A leave-behind for staff meetings, board briefings, and mock surveys."
      />

      {status === "success" ? (
        <div className="card p-10 max-w-2xl fade-up" data-testid="reference-success">
          <CheckCircle2 size={40} strokeWidth={1.5} className="text-success mb-4" />
          <h2 className="text-navy text-2xl font-semibold mb-3">Here's your reference card.</h2>
          <p className="text-ink mb-6 leading-relaxed">
            Thanks {form.name.split(" ")[0]}. Save this file locally — a fresh version arrives with every update
            to the Continuum.
          </p>
          <a
            href="/reference-card.pdf"
            download
            className="btn btn-primary"
            data-testid="reference-download-btn"
          >
            <Download size={14} strokeWidth={2} /> Download reference card (PDF)
          </a>
          <p className="text-xs text-ink-muted mt-4">
            Not receiving it? Reply to the confirmation email and we'll resend.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-8">
          <form onSubmit={submit} className="card p-6 lg:col-span-3 space-y-4" data-testid="reference-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Name</label>
                <input
                  className="field"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="ref-name"
                  required
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  className="field"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="ref-email"
                  required
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Organization</label>
                <input className="field" value={form.organization} onChange={update("organization")} data-testid="ref-org" />
              </div>
              <div>
                <label className="label">Your role</label>
                <input className="field" value={form.role} onChange={update("role")} data-testid="ref-role" />
              </div>
            </div>
            <div>
              <label className="label">One-line problem you're navigating (optional)</label>
              <input className="field" value={form.problem} onChange={update("problem")} data-testid="ref-problem" />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={status === "submitting"}
              data-testid="ref-submit"
            >
              <Download size={14} strokeWidth={2} />
              {status === "submitting" ? "Unlocking..." : "Get the reference card"}
            </button>
            <p className="text-xs text-ink-muted">
              We use your email once to send the reference card and let you know when the Continuum is updated. No
              third-party sharing.
            </p>
          </form>

          <aside className="card p-6 lg:col-span-2 bg-teal-light/40 border-teal/30" data-testid="reference-preview">
            <div className="overline mb-3">What's inside</div>
            <ul className="space-y-2 text-sm text-navy">
              <li>· All 19 frameworks with one-sentence jobs</li>
              <li>· Every role portfolio (primary + secondary frameworks)</li>
              <li>· The six ASAM Dimensions in patient-facing language</li>
              <li>· Ten non-negotiables that apply at every level of care</li>
              <li>· NARR × ASAM continuum map</li>
            </ul>
            <div className="rule my-4 bg-teal/30" />
            <div className="text-xs text-navy">
              Used by directors, BHTs, and boards. Prints cleanly on 11×17.
            </div>
          </aside>
        </div>
      )}
    </PageContainer>
  );
}
